// Error feedback components.
//
// <ErrorBanner>  — slim red notice strip (passive). Use for inline
//                  errors above a content area; no retry action.
// <ErrorPanel>   — centered error block with optional retry button
//                  and accent. Use as a full-area replacement when a
//                  fetch fails and the user can recover.
//
// Both keep the same red palette (light bg + dark red text) regardless
// of theme — error severity is universal.

import type { AccentTokens, ThemeTokens } from '@nayto/theme';
import { FONT_STACK } from '@nayto/theme';

interface ErrorBannerProps {
  message: string;
}

export function ErrorBanner({ message }: ErrorBannerProps) {
  return (
    <div
      style={{
        padding: 12,
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 10,
        fontSize: 12,
        color: '#991b1b',
        marginBottom: 8,
      }}
    >
      {message}
    </div>
  );
}

interface ErrorPanelProps {
  theme: ThemeTokens;
  message: string;
  /** Optional retry button. When provided, accent is required for the
   * button styling. */
  onRetry?: () => void;
  accent?: AccentTokens;
  /** Override the retry-button label. Default 'Try again'. */
  retryLabel?: string;
}

export function ErrorPanel({
  theme,
  message,
  onRetry,
  accent,
  retryLabel = 'Try again',
}: ErrorPanelProps) {
  return (
    <div
      style={{
        padding: '32px 16px',
        textAlign: 'center',
        color: theme.textMuted,
        fontSize: 13,
      }}
    >
      <div style={{ marginBottom: 12, fontFamily: FONT_STACK.mono }}>{message}</div>
      {onRetry && accent && (
        <button
          onClick={onRetry}
          style={{
            padding: '8px 16px',
            borderRadius: 10,
            border: 'none',
            background: accent.main,
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}
